import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Lopettia masthead — vendored from the FastAPI service's `lorenza.html`.
 * Same layout, same class names, same Lopettia SVG wordmark; the CSS in
 * `styles/briefer.css` owns the visual.
 *
 * `demo` shows the play-button "DEMO" badge in the top-right strip (and
 * replaces the YYYY.MM.DD date with `MMM DD, YYYY` to match the original
 * demo-mode rendering).
 */
/** Where the cinematic demo lives. The original FastAPI service still
 *  serves it at /demo; we link out instead of re-implementing in React.
 *  Override via Vite env if the demo moves. */
const DEMO_URL = (import.meta.env.VITE_BRIEFER_DEMO_URL as string | undefined) ?? 'http://localhost:8082/demo'

export function Masthead({ demo = false }: { demo?: boolean }) {
  const { t } = useTranslation()
  const [today, setToday] = useState(formatToday(demo))

  useEffect(() => {
    // Refresh date once a minute so a long-lived tab doesn't show
    // yesterday's date across midnight.
    const interval = window.setInterval(() => setToday(formatToday(demo)), 60_000)
    return () => window.clearInterval(interval)
  }, [demo])

  return (
    <header className="masthead">
      <div className="masthead-top">
        <span>{t('chrome.versionBar', 'BRIEFER · INTAKE INSTRUMENT V2.1')}</span>
        <span className="masthead-top__right">
          {/* Cinematic demo lives on the original FastAPI service at
           * :8082/demo. Keep the link there so we don't have to maintain
           * a separate copy; it opens in a new tab to preserve session. */}
          <a
            className="masthead-demo-btn"
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={t('chrome.demoBtn.tip', 'Watch the 90-second cinematic demo')}
          >
            <span className="masthead-demo-btn__play" aria-hidden="true">
              ▶
            </span>
            {t('chrome.demoBtn', 'Demo')}
          </a>
          {demo && (
            <span className="masthead-top__demo">
              <span className="masthead-top__demo-play" aria-hidden="true">
                ▸
              </span>
              {t('chrome.demo', 'DEMO')}
            </span>
          )}
          <span>{today}</span>
        </span>
      </div>

      <div className="masthead-hero">
        <a href="/" className="lop-logo lop-logo--hero" aria-label="Lopettia home">
          <img src="/brand/lopettia-logo-primary.svg" alt="Lopettia" />
        </a>
        <h1>
          <span>{t('chrome.h1.line1', 'Briefer.')}</span>
          <br />
          <span dangerouslySetInnerHTML={{ __html: t('chrome.h1.line2', 'The <span class="alt">Intent Brief</span>') }} />
          <br />
          <span>{t('chrome.h1.line3', 'for AI applications.')}</span>
        </h1>
      </div>

      <p className="lede">
        {t(
          'chrome.lede',
          'A structured intake instrument from the Lopettia AI Practice. Captures human intent, constraints, stakeholders, and the architectural realities most stakeholders forget to mention until the project is in flight. Hands off a complete brief to the agent fleet — or to your engineering team — with a measurable readiness score.',
        )}
      </p>

      <div
        className="lop-tagline"
        dangerouslySetInnerHTML={{
          __html: t(
            'chrome.tagline',
            '<span class="h">Humans</span> set intent and govern gates · <span class="a">Agents</span> execute · <span class="o">Operations</span> close the loop.',
          ),
        }}
      />

      <div className="meta">
        <span>
          <span>{t('chrome.meta.for', 'FOR')}</span> ·{' '}
          <strong>{t('chrome.meta.forValue', 'Sponsors, Architects, PMs')}</strong>
        </span>
        <span>
          <span>{t('chrome.meta.output', 'OUTPUT')}</span> ·{' '}
          <strong>{t('chrome.meta.outputValue', '8 deliverables · md/json/pptx')}</strong>
        </span>
        <span>
          <span>{t('chrome.meta.sections', 'SECTIONS')}</span> ·{' '}
          <strong>{t('chrome.meta.sectionsValue', '13')}</strong>
        </span>
        <span>
          <span>{t('chrome.meta.estTime', 'EST. TIME')}</span> ·{' '}
          <strong>{t('chrome.meta.estTimeValue', '60–90 min')}</strong>
        </span>
      </div>
    </header>
  )
}

function formatToday(demo: boolean): string {
  const d = new Date()
  if (demo) {
    // MMM DD, YYYY — matches the original demo brief's masthead-top date.
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase()
  }
  // Default: YYYY.MM.DD monospaced.
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}.${m}.${day}`
}
